import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FilterSlideout,
  createDatePresetsSection,
  createMultiSelectSection,
  createSearchSelectSection,
  createSingleSelectSection,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AUDIT_SEVERITY_ORDER, auditSeverityLabelKey } from '../../constants';

const DATE_PRESETS = ['today', '7d', '30d', '90d', 'all'];
const EMPTY_DRAFT = { dateRange: '', severities: [], provider: '', instance: '', actor: '', code: '' };

const draftFromFilters = (filters) => ({
  dateRange: filters.dateRange[0] ?? '',
  severities: filters.severities ?? [],
  provider: filters.provider ?? '',
  instance: filters.instance ?? '',
  actor: filters.actor ?? '',
  code: filters.code ?? '',
});

/**
 * Global Audit "All filters" slideout (CT-4211). Staged apply: sections edit a
 * local draft that is seeded from the live filters each time it opens (so a
 * selection made in the toolbar shows up marked here), and nothing changes the
 * table until "Apply filters" commits the draft. Provider / actor / event-code
 * are searchable selects; instance appears once a provider is chosen.
 */
const GlobalAuditFiltersDrawer = ({ isOpen, onClose, filters, facets, applyFilters }) => {
  const { t } = useTranslation('admin-studio');
  const [draft, setDraft] = useState(() => draftFromFilters(filters));
  const wasOpenRef = useRef(isOpen);

  // Re-seed the draft from the live filters whenever the slideout opens.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync draft on open
      setDraft(draftFromFilters(filters));
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, filters]);

  const setField = useCallback((key, value) => setDraft((d) => ({ ...d, [key]: value })), []);
  // `search-select` is a multi-select (checkbox) section, but provider/actor/code
  // are single-choice server-side. We emulate single by taking the just-toggled
  // value; the controlled `value` re-renders the list as a radio. TODO: swap for a
  // searchable single-select section if `@aimsos/ui` adds one.
  const takeLast = useCallback((key) => (next) => setField(key, next[next.length - 1] ?? ''), [setField]);

  const sections = useMemo(() => {
    const single = (value) => (value ? [value] : []);
    const list = [
      createDatePresetsSection({
        id: 'date',
        title: t('integrations.audit.timeFilter'),
        value: draft.dateRange,
        onChange: (v) => setField('dateRange', v),
        options: DATE_PRESETS.map((p) => ({ value: p, label: t(`integrations.globalAudit.range.${p}`) })),
      }),
      createMultiSelectSection({
        id: 'severity',
        title: t('integrations.audit.severityFilter'),
        value: draft.severities,
        onChange: (next) => setField('severities', next),
        options: AUDIT_SEVERITY_ORDER.map((s) => ({ value: s, label: t(auditSeverityLabelKey(s)) })),
      }),
      createSearchSelectSection({
        id: 'provider',
        title: t('integrations.globalAudit.providerFilter'),
        placeholder: t('integrations.globalAudit.searchOptions'),
        value: single(draft.provider),
        // Switching provider drops any instance scoped to the previous one.
        onChange: (next) => setDraft((d) => ({ ...d, provider: next[next.length - 1] ?? '', instance: '' })),
        options: facets.providers.map((p) => ({ value: p.slug, label: p.name })),
      }),
    ];

    if (draft.provider) {
      list.push(
        createSingleSelectSection({
          id: 'instance',
          title: t('integrations.globalAudit.instanceFilter'),
          value: draft.instance,
          onChange: (v) => setField('instance', v),
          options: facets.instances
            .filter((i) => i.provider_slug === draft.provider)
            .map((i) => ({ value: i.id, label: i.name })),
        }),
      );
    }

    list.push(
      createSearchSelectSection({
        id: 'actor',
        title: t('integrations.globalAudit.actorFilter'),
        placeholder: t('integrations.globalAudit.searchOptions'),
        value: single(draft.actor),
        onChange: takeLast('actor'),
        options: facets.actors.map((a) => ({ value: a, label: a })),
      }),
      createSearchSelectSection({
        id: 'code',
        title: t('integrations.globalAudit.codeFilter'),
        placeholder: t('integrations.globalAudit.searchOptions'),
        value: single(draft.code),
        onChange: takeLast('code'),
        options: facets.codes.map((c) => ({ value: c, label: c })),
      }),
    );

    return list;
  }, [draft, facets, setField, takeLast, t]);

  const i18n = useMemo(
    () => ({
      title: t('integrations.globalAudit.filtersTitle'),
      close: t('integrations.globalAudit.closeFilters'),
      clearAll: t('integrations.globalAudit.clearAll'),
      apply: t('integrations.globalAudit.apply'),
    }),
    [t],
  );

  const handleApply = useCallback(() => {
    applyFilters(draft);
    onClose();
  }, [applyFilters, draft, onClose]);

  return (
    <FilterSlideout
      isOpen={isOpen}
      onClose={onClose}
      size="m"
      sections={sections}
      i18n={i18n}
      onClearAll={() => setDraft(EMPTY_DRAFT)}
      onApply={handleApply}
    />
  );
};

export default GlobalAuditFiltersDrawer;
